import React, { Component } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { VscFilePdf as PdfIcon } from 'react-icons/vsc'
import { IoDownloadOutline as DownloadIcon } from 'react-icons/io5'
import { getLocaleCodeFromUrl, i18nTranslate } from 'src/utils'
import { footerContainer } from 'src/models'
import { APPConfig } from 'config/appConfig'
import { countryData } from '../SignUpForms/countryCodeData'
import { parsePhoneNumberField } from 'src/utils/signUpUtils'
import {
  validateDSAccountId,
  validateDSFirstOrLastName,
  validateDSMobileNumber,
} from './validationSchema'
import './styles.scss'

class DSSearchModal extends Component {
  constructor(props) {
    super(props)
    this.state = {
      dsAccountId: '',
      mobileNumber: '',
      firstName: '',
      lastName: '',
      searchResults: null,
      isSearching: false,
      hasSearched: false,
      searchQuery: '',
      currentPage: 1,
      pageSize: 25,
      hasMoreResults: true,
      isLoadingMore: false,
      dsAccountIdError: '',
      mobileNumberError: '',
      firstNameError: '',
      lastNameError: '',
      countryCode: '',
      isCountryCodeBlurred: true,
    }
    this.resultsListRef = React.createRef()
  }

  componentDidMount() {
    countryData().forEach(country => {
      if (country.value === getLocaleCodeFromUrl().split('_')[1]) {
        this.setState({ countryCode: country.value })
      }
    })
  }
  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.countryCode !== this.state.countryCode &&
      this.state.mobileNumber
    ) {
      this.updateMobileNumberForCountryCode(this.state.countryCode)
    }
  }
  updateMobileNumberForCountryCode = async countryCode => {
    const { cleaned, error } = await validateDSMobileNumber(
      this.state.mobileNumber,
      this.state.countryCode
    )
    this.setState({ mobileNumber: cleaned, mobileNumberError: error })
  }

  handleInputChange = async (field, value) => {
    if (field === 'mobileNumber') {
      const { countryCode } = this.state
      if (countryCode === 'IN') {
        value = value.replace(/[^0-9]/g, '').slice(0, 10)
      } else {
        value = value.replace(/[^0-9+]/g, '').slice(0, 15)
      }
      this.setState({ [field]: value, mobileNumberError: '' })
    } else if (field === 'dsAccountId') {
      const { cleaned, error } = validateDSAccountId(value)
      this.setState({ [field]: cleaned, dsAccountIdError: error })
    } else if (field === 'firstName' || field === 'lastName') {
      const { cleaned, error } = validateDSFirstOrLastName(value, field)
      this.setState({ [field]: cleaned, [`${field}Error`]: error })
    } else {
      this.setState({ [field]: value })
    }
  }
  handleInputBlur = async field => {
    if (field === 'firstName' || field === 'lastName') {
      // Remove trailing spaces when field loses focus
      const value = this.state[field]
      const trimmed = value.replace(/\s+$/g, '')
      this.setState({ [field]: trimmed })
    }
    if (field === 'mobileNumber') {
      const { cleaned, error } = await validateDSMobileNumber(
        this.state.mobileNumber,
        this.state.countryCode
      )
      this.setState({ [field]: cleaned, mobileNumberError: error })
    }
    if (field === 'countryCode') {
      this.setState({ isCountryCodeBlurred: true })
    }
  }
  handleSearch = async (isLoadMore = false) => {
    const {
      dsAccountId,
      mobileNumber,
      firstName,
      lastName,
      currentPage,
      pageSize,
      searchResults,
      searchQuery: existingSearchQuery,
      countryCode,
    } = this.state

    // Build search query immediately to capture current form values
    let searchQuery = existingSearchQuery
    const parsedNumber = await parsePhoneNumberField(mobileNumber, countryCode)
    if (firstName || lastName) {
      const fullName = `${firstName || ''} ${lastName || ''}`.trim()
      searchQuery = fullName || 'Search'
    } else if (dsAccountId) {
      searchQuery = dsAccountId
    } else if (
      mobileNumber &&
      mobileNumber !== '+91' &&
      mobileNumber.length > 3
    ) {
      searchQuery = parsedNumber?.number || mobileNumber
    } else {
      searchQuery = 'Search'
    }

    // Create payload in the required format
    const payload = {
      firstName: firstName || '',
      lastName: lastName || '',
      nuskinAccountId: dsAccountId || '',
      phoneNumber:
        parsedNumber?.number || (mobileNumber.length > 7 ? mobileNumber : ''),
      status: 'active',
    }

    const page = isLoadMore ? currentPage : 1

    this.setState({
      isSearching: true,
      isLoadingMore: isLoadMore,
      searchQuery: searchQuery,
    })

    try {
      const response = await footerContainer.getDSSearchResults({
        payload,
        page,
        size: pageSize,
      })

      setTimeout(() => {
        const newAccounts = response?.brandAffiliateAccounts || []
        const hasMore = newAccounts.length === pageSize

        this.setState({
          searchResults: isLoadMore
            ? {
                ...response,
                brandAffiliateAccounts: [
                  ...(searchResults?.brandAffiliateAccounts || []),
                  ...newAccounts,
                ],
              }
            : response,
          isSearching: false,
          isLoadingMore: false,
          hasSearched: true,
          hasMoreResults: hasMore,
          currentPage: page,
        })
      }, 500)
    } catch (error) {
      console.error('Error fetching search results:', error)
      this.setState({
        isSearching: false,
        isLoadingMore: false,
        hasSearched: !isLoadMore,
      })
    }
  }

  handleClose = () => {
    if (this.props.onClose) {
      this.props.onClose()
    }
  }

  handleDownloadPDF = () => {
    const { directSellerTerminatedListUrl = '' } =
      APPConfig?.getAppConfig() || {}
    // Add the PDF download URL here
    const pdfUrl = directSellerTerminatedListUrl
    window.open(pdfUrl, '_blank')
  }

  handleBack = () => {
    this.setState({
      searchResults: null,
      isSearching: false,
      hasSearched: false,
      searchQuery: '',
      currentPage: 1,
      hasMoreResults: true,
      isLoadingMore: false,
      dsAccountId: '',
      mobileNumber: '',
      firstName: '',
      lastName: '',
      dsAccountIdError: '',
    })
  }

  getActiveSection = () => {
    const { dsAccountId, mobileNumber, firstName } = this.state

    if (dsAccountId) return 'dsAccountId'
    if (mobileNumber && mobileNumber !== '+91') return 'mobileNumber'
    if (firstName) return 'name'
    return null
  }

  isSearchButtonDisabled = () => {
    const {
      dsAccountId,
      mobileNumber,
      firstName,
      isSearching,
      dsAccountIdError,
      mobileNumberError,
      firstNameError,
      lastNameError,
    } = this.state

    if (
      dsAccountIdError ||
      mobileNumberError ||
      firstNameError ||
      lastNameError
    )
      return true

    if (isSearching) return true

    // Check if dsAccountId section meets minimum requirement
    if (dsAccountId && dsAccountId.length >= 7) return false

    if (mobileNumber) return false

    // Check if name section meets minimum requirement
    if (firstName && firstName.length >= 3) return false

    return true
  }

  handleScroll = e => {
    const { hasMoreResults, isLoadingMore, isSearching } = this.state
    const element = e.target
    const bottom =
      element.scrollHeight - element.scrollTop <= element.clientHeight + 50

    if (bottom && hasMoreResults && !isLoadingMore && !isSearching) {
      this.setState(
        prevState => ({ currentPage: prevState.currentPage + 1 }),
        () => this.handleSearch(true)
      )
    }
  }

  renderSearchResults = () => {
    const { searchResults, searchQuery, isLoadingMore } = this.state

    return (
      <div className="search-results-section">
        <div>
          <Button
            className="back-button"
            data-testid="qa-ds-search-back"
            onClick={this.handleBack}>
            &lt; back
          </Button>
        </div>
        <div className="results-header">
          <h3 className="results-title">
            {i18nTranslate(
              'dsSearch.showingResultsFor',
              'Showing Results for',
              {
                isNextGenTranslations: true,
              }
            )}{' '}
            <span className="search-query">"{searchQuery}"</span>
          </h3>
          {/* <p className="results-count">
            {searchResults?.pageableInfo?.count}{' '}
            {searchResults?.pageableInfo?.count === 1 ? 'result' : 'results'}
          </p> */}
        </div>
        <div
          className="results-list"
          onScroll={this.handleScroll}
          ref={this.resultsListRef}>
          {searchResults?.brandAffiliateAccounts?.map((account, index) => (
            <div key={`${account.id}-${index}`} className="result-item">
              <div className="result-info">
                <h4 className="result-name">{account.name}</h4>
                {account.nuskinAccountId && (
                  <p className="result-abo">
                    {i18nTranslate('dsSearch.accountId', 'Account ID', {
                      isNextGenTranslations: true,
                    })}
                    {': '}
                    <strong>{account.nuskinAccountId}</strong>
                  </p>
                )}
                {account.city && <p className="result-city">{account.city}</p>}
                {account.state && (
                  <p className="result-state">{account.state}</p>
                )}
              </div>
            </div>
          ))}
          {isLoadingMore && (
            <div className="loading-more">
              {i18nTranslate(
                'dsSearch.loadingMore',
                'Loading more results...',
                {
                  isNextGenTranslations: true,
                }
              )}
            </div>
          )}
        </div>
      </div>
    )
  }

  renderNoResults = () => {
    return (
      <>
        <div>
          <Button
            className="back-button"
            data-testid="qa-ds-search-back"
            onClick={this.handleBack}>
            &lt; back
          </Button>
        </div>
        <div className="no-results">
          {i18nTranslate('dsSearch.noResults', 'No results found', {
            isNextGenTranslations: true,
          })}
        </div>
      </>
    )
  }

  renderTerminatedListSection = () => {
    return (
      <div
        className="terminated-list-section"
        onClick={this.handleDownloadPDF}
        role="button"
        tabIndex={0}
        onKeyPress={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            this.handleDownloadPDF()
          }
        }}>
        <div className="pdf-icon">
          <PdfIcon size={24} color="#b01616" />
        </div>
        <div className="terminated-list-text">
          <div className="terminated-list-title">
            {i18nTranslate(
              'dsSearch.terminatedList',
              'Delisted Direct Sellers'
            )}
          </div>
        </div>
        <div className="download-icon">
          <DownloadIcon size={24} color="#000" />
        </div>
      </div>
    )
  }

  render() {
    return (
      <Modal
        show={true}
        data-testid="qa-ds-search-modal"
        className="ds-search-modal"
        onHide={this.handleClose}>
        <Modal.Header
          closeButton
          className="ds-search-modal-header"
          data-testid="qa-ds-search-modal-header">
          <Modal.Title
            className="ds-search-modal-title"
            data-testid="qa-ds-search-modal-title">
            {i18nTranslate('dsSearch.title', 'Search Direct Seller Account')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          className="ds-search-modal-body"
          data-testid="qa-ds-search-modal-body">
          <div className="ds-search-form">
            {!this.state.hasSearched && (
              <>
                <div className="form-group">
                  <label className="form-label" htmlFor="dsAccountId">
                    {i18nTranslate('dsSearch.accountId', 'Account ID')}
                  </label>
                  <input
                    id="dsAccountId"
                    type="text"
                    className="form-input"
                    value={this.state.dsAccountId}
                    onChange={e =>
                      this.handleInputChange('dsAccountId', e.target.value)
                    }
                    disabled={
                      this.getActiveSection() &&
                      this.getActiveSection() !== 'dsAccountId'
                    }
                  />
                  {this.state.dsAccountIdError && (
                    <div className="ds-error-message">
                      {this.state.dsAccountIdError}
                    </div>
                  )}
                </div>

                <div className="form-divider">
                  {i18nTranslate('dsSearch.or', 'or')}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="mobileNumber">
                    {i18nTranslate('dsSearch.mobileNumber', 'Mobile Number')}
                  </label>
                  <div className="d-flex">
                    <select
                      id="countryCode"
                      className="form-select country-code-select"
                      value={this.state.countryCode}
                      onBlur={() => this.handleInputBlur('countryCode')}
                      onFocus={() =>
                        this.setState({ isCountryCodeBlurred: false })
                      }
                      onChange={e =>
                        this.handleInputChange('countryCode', e.target.value)
                      }
                      disabled={
                        this.getActiveSection() &&
                        this.getActiveSection() !== 'mobileNumber'
                      }>
                      {countryData().map(option => (
                        <option key={option.value} value={option.value}>
                          {!this.state?.isCountryCodeBlurred
                            ? option.label
                            : option.displayValue}
                        </option>
                      ))}
                    </select>
                    <div className="w-100">
                      <input
                        id="mobileNumber"
                        type="text"
                        className="form-input"
                        value={this.state.mobileNumber}
                        onChange={e =>
                          this.handleInputChange('mobileNumber', e.target.value)
                        }
                        onBlur={() => this.handleInputBlur('mobileNumber')}
                        disabled={
                          this.getActiveSection() &&
                          this.getActiveSection() !== 'mobileNumber'
                        }
                      />
                      {this.state.mobileNumberError && (
                        <div className="ds-error-message mt-2">
                          {this.state.mobileNumberError}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-divider">
                  {i18nTranslate('dsSearch.or', 'or')}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="firstName">
                    {i18nTranslate('dsSearch.firstName', 'First Name')}
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    className="form-input"
                    value={this.state.firstName}
                    onChange={e =>
                      this.handleInputChange('firstName', e.target.value)
                    }
                    onBlur={() => this.handleInputBlur('firstName')}
                    disabled={
                      this.getActiveSection() &&
                      this.getActiveSection() !== 'name'
                    }
                  />
                  {this.state.firstNameError && (
                    <div className="ds-error-message">
                      {this.state.firstNameError}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="lastName">
                    {i18nTranslate('dsSearch.lastName', 'Last Name (Optional)')}
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    className="form-input"
                    value={this.state.lastName}
                    onChange={e =>
                      this.handleInputChange('lastName', e.target.value)
                    }
                    onBlur={() => this.handleInputBlur('lastName')}
                    disabled={
                      this.getActiveSection() &&
                      this.getActiveSection() !== 'name'
                    }
                  />
                  {this.state.lastNameError && (
                    <div className="ds-error-message">
                      {this.state.lastNameError}
                    </div>
                  )}
                </div>
                <Button
                  className="search-button"
                  onClick={this.handleSearch}
                  disabled={this.isSearchButtonDisabled()}>
                  {this.state.isSearching
                    ? i18nTranslate('dsSearch.searching', 'SEARCHING...', {
                        isNextGenTranslations: true,
                      })
                    : i18nTranslate('dsSearch.search', 'SEARCH')}
                </Button>
                {this.renderTerminatedListSection()}
              </>
            )}

            {this.state.hasSearched &&
              this.state.searchResults &&
              this.state.searchResults?.brandAffiliateAccounts?.length > 0 &&
              this.renderSearchResults()}

            {this.state.hasSearched &&
              this.state.searchResults?.message === 'No account found' &&
              this.renderNoResults()}
          </div>
        </Modal.Body>
      </Modal>
    )
  }
}

export default DSSearchModal
export { DSSearchModal }
